/**
 * Chart custom (local) entity creation via dash-api.
 * POST /api/entities/local
 */

import {getAwpApiBase} from '../../utils/awpApiBase';
import {getAwpClientHeaders} from '../../session/awpRequestHeaders';

const AWP_BASE = getAwpApiBase();

/**
 * @typedef {Object} CreateLocalEntityInput
 * @property {string} canonicalName
 * @property {string} entityType
 * @property {string} [description]
 * @property {string[]} [aliases]
 */

/**
 * @typedef {Object} LocalEntitySummary
 * @property {string} canonicalName
 * @property {string} [description]
 * @property {string} entityType
 * @property {string[]} [aliases]
 * @property {string} [source]
 * @property {boolean} [local]
 * @property {number} [internalEntityId]
 */

/**
 * Create a chart-local entity (D… ref) in the knowledge base.
 *
 * @param {CreateLocalEntityInput} input
 * @returns {Promise<{entityRef: string, summary: LocalEntitySummary}>}
 */
export async function createLocalEntity({canonicalName, entityType, description, aliases}) {
    const body = {
        canonicalName,
        entityType,
    };
    if (description != null && String(description).trim()) {
        body.description = String(description).trim();
    }
    if (Array.isArray(aliases) && aliases.length > 0) {
        body.aliases = aliases;
    }

    const res = await fetch(`${AWP_BASE}/api/entities/local`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getAwpClientHeaders(),
        },
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || `HTTP ${res.status}`);
    }

    return res.json();
}

/**
 * Map API response to the entity payload expected by addEntityToViewportCenter /
 * createEntityCardShape / entity-card drag helpers.
 *
 * @param {{entityRef: string, summary?: LocalEntitySummary}} response
 * @returns {Object}
 */
export function mapLocalEntityToCardPayload(response) {
    const entityRef = response?.entityRef;
    const summary = response?.summary || {};
    const label = summary.canonicalName || entityRef || 'Entity';

    return {
        id: entityRef,
        wikidataId: entityRef,
        label,
        canonicalName: label,
        description: summary.description || '',
        entityType: summary.entityType || 'UNKNOWN',
        aliases: Array.isArray(summary.aliases) ? summary.aliases : [],
        local: true,
        source: summary.source || 'chart',
        imageUrl: '',
        relatedCountries: [],
        relatedCountryDetails: [],
    };
}
